import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CreditCard, IndianRupee, CheckCircle2, Receipt, AlertCircle } from 'lucide-react';
import { invoiceService } from '@/services/invoiceService';
import { toast } from 'sonner';

export const PaymentRecordModal = ({
  open,
  onOpenChange,
  invoice,
  onPaymentSuccess,
  onViewReceipt,
}) => {
  const [amount, setAmount] = useState(invoice?.balanceAmount || '');
  const [paymentMode, setPaymentMode] = useState('UPI');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (invoice) {
      setAmount(invoice.balanceAmount || '');
      setPaymentMode('UPI');
      setReferenceNumber('');
      setNotes('');
    }
  }, [invoice]);

  if (!invoice) return null;

  const handleRecord = async () => {
    const payAmt = Number(amount);
    if (!payAmt || payAmt <= 0) {
      toast.error('Please enter a valid payment amount greater than 0');
      return;
    }
    if (payAmt > Number(invoice.balanceAmount) + 0.01) {
      toast.error(
        `Payment amount (₹${payAmt}) exceeds remaining balance of ₹${Number(invoice.balanceAmount).toFixed(2)}`
      );
      return;
    }

    try {
      setSubmitting(true);
      const res = await invoiceService.recordPayment(
        invoice.id,
        {
          amount: payAmt,
          paymentMode,
          referenceNumber: referenceNumber.trim(),
          notes: notes.trim(),
        },
        invoice.clinicId,
        invoice.hospitalId
      );

      toast.success(
        `Payment of ₹${payAmt} recorded successfully! (${res.payment.receiptNumber})`
      );
      onPaymentSuccess(res.invoice, res.payment);
      onOpenChange(false);

      if (onViewReceipt && res.payment) {
        onViewReceipt(res.payment, res.invoice);
      }
    } catch (e) {
      toast.error(e.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="p-5 bg-slate-900 text-white">
          <DialogTitle className="text-base font-bold text-white flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-teal-400" />
            Record Manual Payment
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-300">
            Invoice: <span className="font-mono text-teal-300 font-bold">{invoice.invoiceNumber}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="p-5 space-y-4">
          {/* Balance Card */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2 text-xs">
            <div className="flex justify-between text-slate-600">
              <span>Total Invoice Amount:</span>
              <span className="font-mono font-medium">₹{Number(invoice.totalAmount).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-slate-600">
              <span>Already Paid:</span>
              <span className="font-mono text-emerald-600 font-medium">
                ₹{Number(invoice.paidAmount).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm font-bold text-slate-900 border-t border-slate-200 pt-2">
              <span>Outstanding Balance:</span>
              <span className="font-mono text-red-600">₹{Number(invoice.balanceAmount).toFixed(2)}</span>
            </div>
          </div>

          {/* Amount to pay */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold text-slate-800">
                Payment Amount (₹) <span className="text-red-500">*</span>
              </Label>
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => setAmount(invoice.balanceAmount)}
                className="text-[11px] text-teal-600 h-auto p-0"
              >
                Pay Full Balance
              </Button>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-semibold text-slate-400 text-sm">
                ₹
              </span>
              <Input
                type="number"
                step="any"
                min="1"
                max={invoice.balanceAmount}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="pl-8 text-sm font-mono font-bold"
              />
            </div>
          </div>

          {/* Mode Selector */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-800">
              Payment Mode <span className="text-red-500">*</span>
            </Label>
            <Select value={paymentMode} onValueChange={setPaymentMode}>
              <SelectTrigger className="text-xs">
                <SelectValue placeholder="Select Mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UPI">UPI (GPay / PhonePe / Paytm / QR)</SelectItem>
                <SelectItem value="CASH">Cash Counter</SelectItem>
                <SelectItem value="CARD">Debit / Credit Card (POS Machine)</SelectItem>
                <SelectItem value="BANK_TRANSFER">Direct Bank Transfer / NEFT / IMPS</SelectItem>
                <SelectItem value="CHEQUE">Cheque / Demand Draft</SelectItem>
                <SelectItem value="OTHER">Other Manual Method</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reference Number */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-800">
              Transaction Reference / UTR
              {paymentMode !== 'CASH' && <span className="text-slate-400 font-normal"> (Recommended)</span>}
            </Label>
            <Input
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder={
                paymentMode === 'UPI'
                  ? 'e.g. 12-digit UTR / UPI Ref ID'
                  : paymentMode === 'CARD'
                  ? 'e.g. Auth Code / Card Last 4'
                  : 'Optional external reference'
              }
              className="text-xs"
            />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-800">Internal Billing Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Paid at Reception Desk 2"
              rows={2}
              className="text-xs"
            />
          </div>
        </div>

        <DialogFooter className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
            className="text-xs"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={submitting || !amount || Number(amount) <= 0}
            onClick={handleRecord}
            className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold shadow-sm"
          >
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            {submitting ? 'Recording...' : `Collect ₹${amount ? Number(amount).toFixed(2) : '0.00'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentRecordModal;

